/**
 * アプリケーション全体のレイアウトコンポーネント
 * レスポンシブ・ダークモード対応
 */

import React, { useState } from 'react';
import { Box, AppShell, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { FooterBar } from './FooterBar';
import { FooterProvider } from '../contexts/FooterContext';
import { StaticBackground } from './StaticBackground';

interface AppLayoutProps {
  title?: string;
  icon?: React.ReactNode;
  breadcrumbs?: { label: string; path?: string }[];
  children?: React.ReactNode;
  hideFooter?: boolean; // フッターを非表示にするオプション
  hideSidebar?: boolean; // サイドバーを非表示にするオプション
}

export const AppLayout: React.FC<AppLayoutProps> = ({ title, icon, breadcrumbs, children, hideFooter = false, hideSidebar = false }) => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const [sidebarOpened, setSidebarOpened] = useState(!isMobile);
  const [mobileDrawerOpened, setMobileDrawerOpened] = useState(false);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileDrawerOpened((prev) => !prev);
    } else {
      setSidebarOpened((prev) => !prev);
    }
  };

  const closeMobileDrawer = () => {
    setMobileDrawerOpened(false);
  };

  return (
    <FooterProvider>
    <>
      {/* 静的な背景（アニメーションなし） */}
      <StaticBackground />

      {/* サイドバー */}
      {!hideSidebar && (
        <Sidebar
          opened={sidebarOpened}
          mobileOpened={mobileDrawerOpened}
          onMobileClose={closeMobileDrawer}
        />
      )}

      {/* メインコンテンツエリア */}
      <Box
        style={{
          marginLeft: isMobile ? 0 : (hideSidebar ? 0 : (sidebarOpened ? 240 : 0)),
          transition: 'margin-left 0.3s ease',
          //minHeight: 'calc(100vh - 48px)', // ヘッダー分を除いた高さ
          position: 'relative',
          paddingTop: 48, // ヘッダーの高さ分のパディングを追加
          overflow: 'visible', // スクロール制御をコンテンツに委ねる
        }}
      >
        {/* フローティングヘッダー */}
        <Header 
          onMenuClick={toggleSidebar} 
          title={title} 
          icon={icon}
          breadcrumbs={breadcrumbs}
        />

        {/* コンテンツエリア */}
        <Box
          style={{
            maxWidth: '100vw',
            width: '100%',
            margin: '0 auto',
            height: 'calc(100vh - 95px)', // ヘッダー48px + フッター40px
            paddingBottom: isMobile ? 90 : 0,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {children}
        </Box>
      </Box>

      {/* フッター */}
      <FooterBar />
    </>
    </FooterProvider>
  );
};
